import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Loader2,
  FileDown,
  Trash2,
  Download,
  User,
  ChevronDown,
  AlertTriangle,
  Check,
  X,
  Settings2,
  Copy
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Checkbox } from '../../components/ui/checkbox';
import { formatDateDisplay } from '../../utils/dateUtils';
import { BulkAttendanceDialog, BulkDeleteSlotsDialog, CopyAttendanceDialog, BulkRemoveTeachersFromExportDialog } from '../../components/BulkOperationsDialogs';

// Editable Time Cell Component
const EditableTimeCell = ({ value, onSave, slotId, fieldName }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(slotId, fieldName === 'start_time' ? editValue : null, fieldName === 'end_time' ? editValue : null);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-8 w-24 text-sm"
          autoFocus
          data-testid={`edit-${fieldName}-${slotId}`}
        />
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-emerald-600 hover:text-emerald-700"
          onClick={handleSave}
        >
          <Check className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
          onClick={handleCancel}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <span
      className="cursor-pointer hover:bg-slate-100 px-2 py-1 rounded text-sm font-medium"
      onClick={() => setIsEditing(true)}
      title="Click to edit"
      data-testid={`${fieldName}-${slotId}`}
    >
      {value}
    </span>
  );
};

const ExportSetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exportSet, setExportSet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Bulk operation dialog states
  const [bulkAttendanceDialogOpen, setBulkAttendanceDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [copyAttendanceDialogOpen, setCopyAttendanceDialogOpen] = useState(false);
  const [bulkRemoveTeachersDialogOpen, setBulkRemoveTeachersDialogOpen] = useState(false);
  const [selectedTeachers, setSelectedTeachers] = useState([]);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const response = await api.get(`/export-sets/${id}`);
      setExportSet(response.data);
    } catch (error) {
      toast.error('Failed to fetch export set');
      navigate('/exports');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSlot = async (slotId, isActive) => {
    try {
      await api.patch(`/export-sets/${id}/slots/${slotId}?is_active=${isActive}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update slot');
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await api.delete(`/export-sets/${id}/slots/${slotId}`);
      toast.success('Slot removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete slot');
    }
  };

  const handleToggleAllForTeacher = async (teacherId, activate) => {
    const teacherSlots = exportSet?.slots?.filter(s => s.teacher_id === teacherId) || [];
    
    try {
      for (const slot of teacherSlots) {
        if (slot.is_active !== activate) {
          await api.patch(`/export-sets/${id}/slots/${slot.id}?is_active=${activate}`);
        }
      }
      toast.success(activate ? 'All slots activated' : 'All slots deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update slots');
    }
  };

  const handleUpdateAttendance = async (slotId, attendanceOption) => {
    try {
      await api.patch(`/export-sets/${id}/slots/${slotId}/attendance?attendance_option=${encodeURIComponent(attendanceOption)}`);
      // Update local state
      setExportSet(prev => ({
        ...prev,
        slots: prev.slots.map(s => s.id === slotId ? { ...s, attendance_option: attendanceOption } : s)
      }));
      toast.success('Attendance option updated');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update attendance option';
      toast.error(errorMessage);
    }
  };

  const handleUpdateTimes = async (slotId, startTime, endTime) => {
    try {
      const params = new URLSearchParams();
      if (startTime) params.append('start_time', startTime);
      if (endTime) params.append('end_time', endTime);
      
      await api.patch(`/export-sets/${id}/slots/${slotId}/times?${params.toString()}`);
      // Update local state
      setExportSet(prev => ({
        ...prev,
        slots: prev.slots.map(s => s.id === slotId ? { 
          ...s, 
          ...(startTime && { start_time: startTime.toUpperCase() }),
          ...(endTime && { end_time: endTime.toUpperCase() })
        } : s)
      }));
      toast.success('Time updated');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update time';
      toast.error(errorMessage);
    }
  };

  const handleGenerateExport = async () => {
    setGenerating(true);
    try {
      const response = await api.post(`/export-sets/${id}/generate`);
      toast.success('Export file generated!');
      
      // Offer to download
      const downloadResponse = await api.get(`/export-files/${response.data.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', response.data.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to generate export');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Group slots by teacher
  const slotsByTeacher = exportSet?.slots?.reduce((acc, slot) => {
    if (!acc[slot.teacher_id]) {
      acc[slot.teacher_id] = {
        teacher_id: slot.teacher_id,
        teacher_name: slot.teacher_name,
        slots: []
      };
    }
    acc[slot.teacher_id].slots.push(slot);
    return acc;
  }, {}) || {};

  const teacherGroups = Object.values(slotsByTeacher).sort((a, b) => 
    a.teacher_name.localeCompare(b.teacher_name)
  );

  const activeSlots = exportSet?.slots?.filter(s => s.is_active).length || 0;
  const totalSlots = exportSet?.slots?.length || 0;
  const teacherCount = teacherGroups.length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="export-set-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/exports')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Exports
            </Button>
            <div>
              <h1 className="font-outfit text-2xl font-bold text-slate-900">{exportSet?.friendly_name}</h1>
              <p className="text-slate-500">
                {teacherCount} teachers · {activeSlots} of {totalSlots} slots active
              </p>
            </div>
          </div>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={handleGenerateExport}
            disabled={generating || activeSlots === 0}
            data-testid="generate-csv-btn"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Generate & Download CSV
              </>
            )}
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Teachers</p>
              <p className="text-2xl font-bold text-slate-900">{teacherCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Active Slots</p>
              <p className="text-2xl font-bold text-emerald-600">{activeSlots}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Slots</p>
              <p className="text-2xl font-bold text-slate-600">{totalSlots}</p>
            </CardContent>
          </Card>
        </div>

        {/* Bulk Operations Toolbar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-slate-700">Bulk Operations:</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setBulkAttendanceDialogOpen(true)}
                data-testid="bulk-attendance-btn"
              >
                <Settings2 className="h-4 w-4 mr-1" />
                Change Attendance
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCopyAttendanceDialogOpen(true)}
                data-testid="copy-attendance-btn"
              >
                <Copy className="h-4 w-4 mr-1" />
                Copy Config
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setBulkDeleteDialogOpen(true)}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete Slots
              </Button>
              {selectedTeachers.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setBulkRemoveTeachersDialogOpen(true)}
                  data-testid="bulk-remove-teachers-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove {selectedTeachers.length} Teacher(s)
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Teacher Slots Accordion */}
        {teacherGroups.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileDown className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No slots in this export set</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-outfit">Conference Slots by Teacher</CardTitle>
                  <CardDescription>
                    Expand each teacher to view and manage their time slots
                  </CardDescription>
                </div>
                {selectedTeachers.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setSelectedTeachers([])}
                    className="text-slate-500"
                  >
                    Clear Selection
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" className="space-y-2">
                {teacherGroups.map((group) => {
                  const activeCount = group.slots.filter(s => s.is_active).length;
                  const totalCount = group.slots.length;
                  // Check if teacher has room (from first slot)
                  const teacherHasRoom = group.slots[0]?.teacher_has_room;
                  const isSelected = selectedTeachers.includes(group.teacher_id);
                  
                  return (
                    <AccordionItem 
                      key={group.teacher_id} 
                      value={group.teacher_id}
                      className={`border rounded-lg px-4 ${isSelected ? 'border-indigo-300 bg-indigo-50/50' : ''}`}
                    >
                      <AccordionTrigger className="hover:no-underline py-4">
                        <div className="flex items-center justify-between w-full pr-4">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedTeachers([...selectedTeachers, group.teacher_id]);
                                } else {
                                  setSelectedTeachers(selectedTeachers.filter(id => id !== group.teacher_id));
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`select-teacher-${group.teacher_id}`}
                            />
                            <div className="bg-indigo-100 p-2 rounded-full">
                              <User className="h-4 w-4 text-indigo-600" />
                            </div>
                            <div className="text-left">
                              <p className="font-medium text-slate-900">{group.teacher_name}</p>
                              <p className="text-sm text-slate-500">
                                {activeCount} of {totalCount} slots active
                                {teacherHasRoom === false && (
                                  <span className="ml-2 text-amber-600">· No room assigned</span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {teacherHasRoom === false && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>No room assigned - only Remote Only is available</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <Badge variant={activeCount === totalCount ? 'default' : activeCount === 0 ? 'outline' : 'secondary'}>
                              {activeCount}/{totalCount}
                            </Badge>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pb-4">
                          {/* Bulk Actions for this teacher */}
                          <div className="flex gap-2 mb-4 pt-2 border-t">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleToggleAllForTeacher(group.teacher_id, true)}
                              disabled={activeCount === totalCount}
                            >
                              Activate All
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleToggleAllForTeacher(group.teacher_id, false)}
                              disabled={activeCount === 0}
                            >
                              Deactivate All
                            </Button>
                          </div>
                          
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Start Time</TableHead>
                                <TableHead>End Time</TableHead>
                                <TableHead>Attendance</TableHead>
                                <TableHead>Active</TableHead>
                                <TableHead className="text-right">Action</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {group.slots.map((slot) => (
                                <TableRow 
                                  key={slot.id} 
                                  className={!slot.is_active ? 'opacity-50 bg-slate-50' : ''}
                                >
                                  <TableCell>{formatDateDisplay(slot.conference_date)}</TableCell>
                                  <TableCell>
                                    <EditableTimeCell
                                      value={slot.start_time}
                                      onSave={handleUpdateTimes}
                                      slotId={slot.id}
                                      fieldName="start_time"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <EditableTimeCell
                                      value={slot.end_time}
                                      onSave={handleUpdateTimes}
                                      slotId={slot.id}
                                      fieldName="end_time"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Select
                                        value={slot.attendance_option || 'In-person Only'}
                                        onValueChange={(v) => handleUpdateAttendance(slot.id, v)}
                                      >
                                        <SelectTrigger className="w-[160px]" data-testid={`attendance-${slot.id}`}>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="Remote Only">Remote Only</SelectItem>
                                          <SelectItem 
                                            value="In-person Only"
                                            disabled={slot.teacher_has_room === false}
                                          >
                                            In-person Only {slot.teacher_has_room === false && '(needs room)'}
                                          </SelectItem>
                                          <SelectItem 
                                            value="Attendee Choice"
                                            disabled={slot.teacher_has_room === false}
                                          >
                                            Attendee Choice {slot.teacher_has_room === false && '(needs room)'}
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                      {slot.teacher_has_room === false && slot.attendance_option !== 'Remote Only' && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger>
                                              <AlertTriangle className="h-4 w-4 text-amber-500" />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>Teacher has no room assigned. Only "Remote Only" is available.</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Switch
                                      checked={slot.is_active}
                                      onCheckedChange={(checked) => handleToggleSlot(slot.id, checked)}
                                      data-testid={`toggle-export-slot-${slot.id}`}
                                    />
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-rose-600 hover:text-rose-700"
                                      onClick={() => handleDeleteSlot(slot.id)}
                                      data-testid={`delete-export-slot-${slot.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
                              </Accordion>
            </CardContent>
          </Card>
        )}

        {/* Bulk Attendance Dialog */}
        <BulkAttendanceDialog
          open={bulkAttendanceDialogOpen}
          onOpenChange={setBulkAttendanceDialogOpen}
          exportSetId={id}
          selectedSlots={[]}
          onSuccess={fetchData}
        />

        {/* Bulk Delete Slots Dialog */}
        <BulkDeleteSlotsDialog
          open={bulkDeleteDialogOpen}
          onOpenChange={setBulkDeleteDialogOpen}
          exportSetId={id}
          slots={exportSet?.slots || []}
          onSuccess={fetchData}
        />

        {/* Copy Attendance Config Dialog */}
        <CopyAttendanceDialog
          open={copyAttendanceDialogOpen}
          onOpenChange={setCopyAttendanceDialogOpen}
          exportSetId={id}
          teachers={teacherGroups.map(g => ({ 
            id: g.teacher_id, 
            first_name: g.teacher_name.split(' ')[0],
            last_name: g.teacher_name.split(' ').slice(1).join(' ')
          }))}
          onSuccess={fetchData}
        />

        {/* Bulk Remove Teachers Dialog */}
        <BulkRemoveTeachersFromExportDialog
          open={bulkRemoveTeachersDialogOpen}
          onOpenChange={setBulkRemoveTeachersDialogOpen}
          exportSetId={id}
          selectedTeachers={selectedTeachers}
          onSuccess={() => {
            setSelectedTeachers([]);
            fetchData();
          }}
        />
      </div>
    </DashboardLayout>
  );
};

export default ExportSetDetailPage;
